/*
 * MemoryPage — Progress, "My learning": one report page answering what I've
 * learned, how well it's holding, whether I'm getting interview-ready, and
 * what to do next. Sections: learning overview (with a readiness hero),
 * activity (heatmap + weekly trend), skills & topics (role-scoped, with a
 * skill → topic → concept detail sheet), learning memory (preview + the
 * Explore overlay), interview readiness (role radar), strengths & areas to
 * improve (evidence + gap types), practice performance, insights, next steps,
 * goals and recent activity.
 *
 * Real data only (memory tree, engine snapshot, hub, goal, interview history
 * in this browser); derivations live in progressReport.js. What isn't
 * recorded — learning time, question types, history older than the recent
 * events — is labelled as such rather than estimated.
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import '../../styles/memory.css';
import '../../styles/prepare-up-home.css';
import '../../styles/prepare-up-hub.css';
import '../../styles/prepare-up-practice.css';
import '../../styles/prepare-up-progress.css';
import APIService from '../../services/APIService';
import * as sessionStore from './sessionStore';
import PracticeChooser from './PracticeChooser';
import { Bar, HomeSearch, ThemeToggle, buildSearchEntries, loadGoal } from './HomeDashboard';
import { buildPaths, getHubTreeCached, indexTree } from './paths';
import { practiceDays, streakFrom } from './progressData';
import { useAuth } from '../../context/AuthContext';
import { ROLES, loadHistory, roleForGoal, typeLabel } from './interviewData';
import { loadPrefs, savePrefs } from './settingsPrefs';
import {
  MIN_EVIDENCE, STATUS as STATUS_R, byDifficulty, leavesOf as leavesOfNode, coverageOf, dayDetail, gapsOf, improvementsOf, proficiency,
  readinessOf, recurringInterviewGap, skillRows, statusOf, strengthsOf, totalsOf,
} from './progressReport';
import {
  IconArrowRight, IconArrowUp, IconBook, IconBrain, IconChevDown, IconChevRight, IconCode, IconGraph, IconMemory, IconSpark, IconX,
} from './icons';

const STATUS = {
  new: { label: 'New', cls: 'new', word: 'Not started yet' },
  learning: { label: 'Learning', cls: 'learning', word: 'Getting started' },
  weak: { label: 'Weak', cls: 'weak', word: 'Needs work' },
  fading: { label: 'Fading', cls: 'fading', word: 'Fading — worth a refresh' },
  strong: { label: 'Strong', cls: 'strong', word: 'Going strong' },
};
const st = (s) => STATUS[s] || STATUS.new;
const pct = (v) => `${Math.round((v || 0) * 100)}%`;
const ago = (d) => (d == null ? 'never practised' : d < 1 ? 'today' : d < 2 ? 'yesterday' : d < 14 ? `${Math.round(d)} days ago` : `${Math.round(d / 7)} weeks ago`);
const num = (v) => new Intl.NumberFormat().format(Math.round(Number(v) || 0));

// Memory-strength bands (shown as words, not colour alone).
const band = (m) => (m == null ? { key: 'new', label: 'Not practised' }
  : m >= 0.8 ? { key: 'strong', label: 'Strong' }
    : m >= 0.6 ? { key: 'good', label: 'Good' }
      : m >= 0.4 ? { key: 'needs', label: 'Needs practice' }
        : { key: 'weak', label: 'Weak' });


/* ── pieces kept from the original memory view ─────────────────────────── */
const StatusChip = ({ status }) => (
  <span className={`mem-chip ${st(status).cls}`}>{st(status).label}</span>
);
const MasteryBar = ({ mastery, status }) => (
  <span className="mem-bar" role="img" aria-label={`Mastery ${pct(mastery)}`}>
    <i className={st(status).cls} style={{ width: pct(mastery) }} />
  </span>
);
const DepthDots = ({ reached = 0, max = 0 }) => (
  max > 0 ? (
    <span className="mem-dots" title={`Depth ${reached} of ${max} — foundational → nuance`}>
      {Array.from({ length: Math.min(max, 7) }, (_, i) => (
        <i key={i} className={i < reached ? 'on' : ''} />
      ))}
    </span>
  ) : null
);
const Row = ({ node, depth, onOpen, expanded, toggle }) => {
  const hasKids = node.children && node.children.length > 0;
  const isOpen = expanded.has(node.id);
  const untouched = node.attempts === 0;
  return (
    <>
      <div className={`mem-row d${depth}${untouched ? ' dim' : ''}`}>
        {hasKids ? (
          <button className="mem-caret" type="button" onClick={() => toggle(node.id)}
            aria-expanded={isOpen} aria-label={isOpen ? 'Collapse' : 'Expand'}>
            {isOpen ? '▾' : '▸'}
          </button>
        ) : <span className="mem-caret" />}
        {hasKids ? (
          <span className="mem-name">{node.name}</span>
        ) : (
          <button className="mem-name leaf" type="button" onClick={() => onOpen(node)}>{node.name}</button>
        )}
        <StatusChip status={node.status} />
        <MasteryBar mastery={node.mastery} status={node.status} />
        <span className="mem-meta">{node.attempts > 0 ? `${node.correct}/${node.attempts}` : '—'}</span>
        {!hasKids ? <DepthDots reached={node.depth_reached} max={node.depth_max} /> : (
          node.weak > 0 ? <span className="mem-weakcount">{node.weak} weak</span> : <span />
        )}
      </div>
      {hasKids && isOpen && node.children.map((c) => (
        <Row key={c.id} node={c} depth={depth + 1} onOpen={onOpen} expanded={expanded} toggle={toggle} />
      ))}
    </>
  );
};

/* ── Learning memory map: skills orbit the overall memory ──────────────── */
const MemoryMap = ({ skills, overall, onSelect, selected, large = false, centerName = 'Overall memory', hoverSelect = true, sizeBy = 'attempts' }) => {
  const W = large ? 560 : 440; const H = large ? 360 : 280;
  const cx = W / 2; const cy = H / 2; const R = large ? 135 : 105;
  const maxA = Math.max(1, ...skills.map((s) => s.attempts || 0));
  const placed = skills.map((s, i) => {
    const a = (-Math.PI / 2) + (i / Math.max(1, skills.length)) * Math.PI * 2 + 0.35;
    return { ...s, x: cx + Math.cos(a) * R * 1.12, y: cy + Math.sin(a) * R * 0.86, r: sizeBy === 'mastery'
      ? (s.attempts ? 10 + (s.mastery || 0) * 9 : 8)          // size follows memory strength, only slightly
      : 9 + Math.sqrt((s.attempts || 0) / maxA) * (large ? 16 : 13) };
  });
  return (
    <div className="pg-map" style={{ aspectRatio: `${W} / ${H}` }}>
      <svg viewBox={`0 0 ${W} ${H}`} aria-hidden="true">
        <ellipse cx={cx} cy={cy} rx={R * 1.12} ry={R * 0.86} className="pg-orbit" />
        <ellipse cx={cx} cy={cy} rx={R * 0.62} ry={R * 0.5} className="pg-orbit faint" />
        {placed.map((s) => <line key={s.id} x1={cx} y1={cy} x2={s.x} y2={s.y} className="pg-link" />)}
        <circle cx={cx} cy={cy} r={large ? 54 : 44} className="pg-core" />
      </svg>
      <div className="pg-core-label" style={{ left: `${(cx / W) * 100}%`, top: `${(cy / H) * 100}%` }}>
        <b>{overall == null ? '—' : pct(overall)}</b><small>{centerName}</small>
      </div>
      {placed.map((s) => {
        const b = band(s.attempts ? s.mastery : null);
        return (
          <button key={s.id} type="button" className={`pg-node b-${b.key}${selected === s.id ? ' on' : ''}`}
            style={{ left: `${(s.x / W) * 100}%`, top: `${(s.y / H) * 100}%`, '--r': `${s.r * 2}px` }}
            onClick={() => onSelect(s.id)} onMouseEnter={hoverSelect ? () => onSelect(s.id) : undefined} onFocus={hoverSelect ? () => onSelect(s.id) : undefined}
            aria-label={`${s.name}: ${s.attempts ? `${pct(s.mastery)} memory strength, ${b.label}` : 'not practised'}`}>
            <i aria-hidden="true" />
            <span><b>{s.name}</b><em>{s.attempts ? pct(s.mastery) : '—'}</em></span>
          </button>
        );
      })}
    </div>
  );
};

const Legend = () => (
  <ul className="pg-legend" aria-label="Memory strength bands">
    <li><i className="b-strong" />Strong (≥ 80%)</li>
    <li><i className="b-good" />Good (60–79%)</li>
    <li><i className="b-needs" />Needs practice (40–59%)</li>
    <li><i className="b-weak" />Weak (&lt; 40%)</li>
  </ul>
);

// Longest run of consecutive practice days in the (lower-bound) day set.
const longestRun = (days) => {
  let best = 0;
  days.forEach((d) => {
    if (days.has(d - 1)) return;            // only start counting at a run's newest day
    let n = 0;
    while (days.has(d + n)) n += 1;
    best = Math.max(best, n);
  });
  return best;
};

/* ── Explore memory: a deeper layer over the Progress page ─────────────── */
const SKILL_ICON = { ai: IconBrain, ml: IconGraph, dl: IconSpark, dsa: IconCode };
const SkillIcon = ({ id }) => {
  const I = SKILL_ICON[String(id).split('.')[0]] || IconBook;
  return <span className="pg-lm-icon" aria-hidden="true"><I size={15} /></span>;
};
const EX_VIEWS = [['overview', 'Overview'], ['skills', 'Skills'], ['topics', 'Topics'], ['gaps', 'Knowledge Gaps'], ['recs', 'Recommendations']];
const held = (n) => (n && n.attempts ? n.mastery : null);
const kids = (n) => (n && n.children) || [];
// Practised first (most practised), then the rest in their natural order.
const byPractice = (list) => [...(list || [])].sort((a, b) => (b.attempts > 0) - (a.attempts > 0) || (b.attempts || 0) - (a.attempts || 0));
// Practised, weakest first; then not-yet-practised.
const byWeakest = (list) => [...(list || [])].sort((a, b) => (b.attempts > 0) - (a.attempts > 0) || (a.mastery || 0) - (b.mastery || 0));
// How much a node needs attention: low strength, weighted by how much evidence backs it.
const needScore = (n) => (1 - (n.mastery || 0)) * Math.log2(1 + (n.attempts || 0));
// Why a node needs reinforcing — only from what the engine recorded.
const whyWeak = (n) => {
  const why = [];
  if (n.attempts >= 4 && n.correct / n.attempts < 0.5) why.push(`Frequently answered incorrectly — ${n.correct} of ${n.attempts} right`);
  else if (n.attempts > 0 && n.attempts < 4) why.push(`Only practised ${n.attempts} time${n.attempts === 1 ? '' : 's'} so far`);
  if (n.status === 'fading') why.push(`Fading — last practised ${ago(n.last_seen_days)}`);
  if ((n.gaps || []).length) why.push(`Recently missed: ${n.gaps[0]}`);
  else if (n.weak > 0 && kids(n).length) why.push(`${n.weak} weak concept${n.weak === 1 ? '' : 's'} inside`);
  return why.length ? why : ['Memory strength is still low'];
};

const NodeTable = ({ list, level, showSkill, onOpen, onDetails, onRead, onPractice }) => (
  <div className="pg-ex-table" role="table" aria-label={`${level === 'skill' ? 'Skills' : 'Topics'} in your memory`}>
    <div className="pg-ex-row head" role="row">
      <span role="columnheader">{level === 'skill' ? 'Skill' : 'Topic'}</span>
      <span role="columnheader">Strength</span>
      <span role="columnheader">Questions</span>
      <span role="columnheader">Correct</span>
      <span role="columnheader">Weak</span>
      <span role="columnheader">Last practised</span>
      <span role="columnheader">Status</span>
      <span role="columnheader"><span className="pu-sr">Actions</span></span>
    </div>
    {list.map((n) => {
      const b = band(held(n));
      const canOpen = kids(n).length > 0;
      return (
        <div key={n.id} className={`pg-ex-row${n.attempts ? '' : ' dim'}`} role="row">
          <span role="cell" className="pg-ex-name">
            <button type="button" onClick={() => (canOpen ? onOpen(n) : onDetails(n))}>{n.name}</button>
            {showSkill && n.root && <small>{n.root.name}</small>}
          </span>
          <span role="cell" className={`pg-ex-strength b-${b.key}`}><Bar value={held(n) || 0} label={`${n.name}: ${b.label}`} /><b>{n.attempts ? pct(n.mastery) : '—'}</b></span>
          <span role="cell">{num(n.attempts)}</span>
          <span role="cell">{num(n.correct)}</span>
          <span role="cell">{n.weak || 0}</span>
          <span role="cell">{n.attempts ? ago(n.last_seen_days) : '—'}</span>
          <span role="cell"><span className={`pg-band b-${b.key}`}>{b.label}</span></span>
          <span role="cell" className="pg-ex-acts">
            {canOpen
              ? <button type="button" className="pu-link" onClick={() => onOpen(n)}>{level === 'skill' ? 'See topics' : 'Explore'}</button>
              : <button type="button" className="pu-link" onClick={() => onDetails(n)}>Details</button>}
            {level !== 'skill' && <button type="button" className="pu-link" onClick={() => onRead(n)}>Read</button>}
            <button type="button" className="pu-link" onClick={() => onPractice(n)}>Practice <IconArrowRight size={12} /></button>
          </span>
        </div>
      );
    })}
  </div>
);

const MemoryExplorer = ({ tree, overall, due, nameOf, trail, recs, initial, onClose, onPractice, onRead, onDetails, expanded, toggle }) => {
  const byId = useMemo(() => indexTree(tree), [tree]);
  // Node ids are dotted paths (ai.rag.chunking) — their prefixes are the ancestors.
  const pathTo = useCallback((id) => {
    const parts = String(id).split('.');
    const out = [];
    for (let i = 1; i <= parts.length; i += 1) { const k = parts.slice(0, i).join('.'); if (byId[k]) out.push(k); }
    return out.length ? out : [id];
  }, [byId]);
  const [view, setView] = useState(initial?.view || 'overview');
  const [focus, setFocus] = useState(() => (initial?.sel ? pathTo(initial.sel).slice(0, -1) : initial?.skill ? [initial.skill] : []));
  const [sel, setSel] = useState(initial?.sel || null);
  const [showTree, setShowTree] = useState(false);
  const [topicSkill, setTopicSkill] = useState('all');
  const [topicScope, setTopicScope] = useState('practised');

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const allTopics = useMemo(() => tree.flatMap((r) => kids(r).map((c) => ({ ...c, root: r }))), [tree]);
  const totals = tree.reduce((a, t) => ({ q: a.q + (t.attempts || 0), c: a.c + (t.correct || 0), w: a.w + (t.weak || 0) }), { q: 0, c: 0, w: 0 });

  const focusNode = focus.length ? byId[focus[focus.length - 1]] : null;
  const orbitAll = byPractice(focusNode ? kids(focusNode) : tree);
  const orbit = orbitAll.slice(0, 8);
  const selNode = sel ? byId[sel] : focusNode;          // null → overall memory
  const levelOf = (n) => ['Skill', 'Topic', 'Subtopic'][Math.min(2, pathTo(n.id).length - 1)];

  const goOverall = () => { setView('overview'); setFocus([]); setSel(null); };
  const into = (n) => {                                   // centre the map on n (or its parent, for a leaf)
    setView('overview');
    const p = pathTo(n.id);
    if (kids(n).length) { setFocus(p); setSel(null); } else { setFocus(p.slice(0, -1)); setSel(n.id); }
  };
  const openChild = (c) => {                              // from the detail panel: one level deeper
    if (!selNode) { into(c); return; }
    setFocus(pathTo(selNode.id)); setSel(c.id);
  };
  const up = () => { setSel(focus[focus.length - 1] || null); setFocus(focus.slice(0, -1)); };
  const pickOrbit = (id) => {
    if (sel === id && kids(byId[id]).length) { setFocus(pathTo(id)); setSel(null); } else setSel(id);
  };

  /* knowledge gaps: practised topics and subtopics that aren't holding */
  const gaps = useMemo(() => {
    const out = [];
    const walk = (nodes, depth) => nodes.forEach((n) => {
      if (depth > 0 && n.attempts > 0 && (['weak', 'fading'].includes(n.status) || (n.mastery || 0) < 0.4)) out.push(n);
      walk(kids(n), depth + 1);
    });
    walk(tree, 0);
    return out.sort((a, b) => needScore(b) - needScore(a)).slice(0, 12);
  }, [tree]);

  /* recommendations, from the same data */
  const practisedTopics = allTopics.filter((t) => t.attempts > 0);
  const strongest = [...practisedTopics].sort((a, b) => (b.mastery || 0) - (a.mastery || 0))[0];
  const weakest = [...practisedTopics].filter((t) => t.id !== strongest?.id).sort((a, b) => needScore(b) - needScore(a))[0];
  const focusOn = weakest ? byWeakest(kids(weakest)).slice(0, 2) : [];
  const nextUp = focusOn[0] || (due[0] ? { id: due[0].concept_id, name: nameOf(due[0].concept_id) } : null);

  const topicRows = allTopics
    .filter((t) => (topicSkill === 'all' || t.root.id === topicSkill) && (topicScope === 'all' || t.attempts > 0))
    .sort((a, b) => (b.attempts > 0) - (a.attempts > 0) || needScore(b) - needScore(a));

  const statRow = (label, value) => <div><dt>{label}</dt><dd>{value}</dd></div>;

  const detail = selNode ? (() => {
    const b = band(held(selNode));
    const list = byPractice(kids(selNode));
    const centred = focus[focus.length - 1] === selNode.id;
    return (
      <>
        <span className="pg-lm-kicker">{levelOf(selNode)}</span>
        <div className="pg-lm-title"><h3>{selNode.name}</h3><span className={`pg-band b-${b.key}`}>{b.label}</span></div>
        <div className={`pg-lm-big b-${b.key}`}><b>{selNode.attempts ? pct(selNode.mastery) : '—'}</b><span>Memory strength</span></div>
        <dl className="pg-lm-stats">
          {statRow('Questions practised', num(selNode.attempts))}
          {statRow('Answered correctly', selNode.attempts ? `${num(selNode.correct)} (${pct(selNode.correct / selNode.attempts)})` : '—')}
          {statRow('Weak concepts', selNode.weak || 0)}
          {statRow('Last practised', selNode.attempts ? ago(selNode.last_seen_days) : 'Not yet')}
        </dl>
        {selNode.attempts > 0 && ['weak', 'needs'].includes(b.key) && <p className="pg-lm-why">{whyWeak(selNode)[0]}</p>}
        <div className="pg-lm-acts">
          {list.length > 0 && !centred && (
            <button type="button" className="pu-btn pu-btn-sm pu-btn-quiet" onClick={() => { setFocus(pathTo(selNode.id)); setSel(null); }}>
              See {pathTo(selNode.id).length === 1 ? 'topics' : 'subtopics'}
            </button>
          )}
          <button type="button" className="pu-btn pu-btn-sm pu-btn-primary" onClick={() => onPractice(selNode)}>Practice</button>
          {pathTo(selNode.id).length > 1 && <button type="button" className="pu-link" onClick={() => onRead(selNode)}>Read</button>}
          {!list.length && <button type="button" className="pu-link" onClick={() => onDetails(selNode)}>Details</button>}
        </div>
        {list.length > 0 && (
          <section className="pg-lm-subs" aria-label={`Key ${pathTo(selNode.id).length === 1 ? 'topics' : 'subtopics'}`}>
            <h4>Key {pathTo(selNode.id).length === 1 ? 'topics' : 'subtopics'}</h4>
            <ul>
              {list.map((c) => {
                const cb = band(held(c));
                return (
                  <li key={c.id}>
                    <button type="button" className={`b-${cb.key}`} onClick={() => openChild(c)}>
                      <span>{c.name}</span><Bar value={held(c) || 0} label={`${c.name}: ${cb.label}`} /><b>{c.attempts ? pct(c.mastery) : '—'}</b>
                    </button>
                  </li>
                );
              })}
            </ul>
          </section>
        )}
      </>
    );
  })() : (
    <>
      <span className="pg-lm-kicker">All skills</span>
      <div className="pg-lm-title"><h3>Overall memory</h3><span className={`pg-band b-${band(overall).key}`}>{band(overall).label}</span></div>
      <div className={`pg-lm-big b-${band(overall).key}`}><b>{overall == null ? '—' : pct(overall)}</b><span>Average memory strength</span></div>
      <dl className="pg-lm-stats">
        {statRow('Questions practised', num(totals.q))}
        {statRow('Answered correctly', totals.q ? `${num(totals.c)} (${pct(totals.c / totals.q)})` : '—')}
        {statRow('Weak concepts', totals.w)}
        {statRow('Skills practised', `${tree.filter((t) => t.attempts > 0).length} of ${tree.length}`)}
      </dl>
      <p className="pu-meta pg-lm-hint">Select a skill to see its topics, or a node in the map to see its memory.</p>
    </>
  );

  return (
    <div className="kh-modal-veil pg-lm-veil" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="kh-modal pg-lm" role="dialog" aria-modal="true" aria-labelledby="pg-lm-title">
        <div className="kh-modal-head pg-lm-head">
          <div>
            <h2 id="pg-lm-title">Learning Memory</h2>
            <p>An interactive view of what you’ve learned, what’s strong, and what needs reinforcement.</p>
          </div>
          <button type="button" className="kh-icon-btn pg-lm-close" onClick={onClose} aria-label="Close Learning Memory"><IconX size={18} /><span>Close</span></button>
        </div>

        <nav className="pg-lm-tabs" role="tablist" aria-label="Learning Memory views">
          {EX_VIEWS.map(([id, label]) => (
            <button key={id} type="button" role="tab" aria-selected={view === id} className={view === id ? 'on' : ''} onClick={() => setView(id)}>
              {label}{id === 'gaps' && gaps.length ? <small>{gaps.length}</small> : null}
            </button>
          ))}
        </nav>

        {view === 'overview' && (
          <div className="pg-lm-body">
            <aside className="pg-lm-col pg-lm-skills" aria-label="Your skills">
              <button type="button" className={`pg-lm-skill${!focus.length ? ' on' : ''}`} onClick={goOverall} aria-pressed={!focus.length}>
                <span className="pg-lm-icon" aria-hidden="true"><IconMemory size={15} /></span>
                <span className="pg-lm-skill-main">
                  <span className="pg-lm-skill-top"><b>Overall memory</b><em>{overall == null ? '—' : pct(overall)}</em></span>
                  <span className={`b-${band(overall).key}`}><Bar value={overall || 0} label={`Overall: ${band(overall).label}`} /></span>
                  <small>{band(overall).label}</small>
                </span>
              </button>
              {tree.map((s) => {
                const b = band(held(s));
                return (
                  <button key={s.id} type="button" className={`pg-lm-skill${focus[0] === s.id ? ' on' : ''}`} aria-pressed={focus[0] === s.id}
                    onClick={() => { setView('overview'); setFocus([s.id]); setSel(null); }}>
                    <SkillIcon id={s.id} />
                    <span className="pg-lm-skill-main">
                      <span className="pg-lm-skill-top"><b>{s.name}</b><em>{s.attempts ? pct(s.mastery) : '—'}</em></span>
                      <span className={`b-${b.key}`}><Bar value={held(s) || 0} label={`${s.name}: ${b.label}`} /></span>
                      <small>{b.label}{s.weak ? ` · ${s.weak} weak` : ''}</small>
                    </span>
                  </button>
                );
              })}
            </aside>

            <section className="pg-lm-col pg-lm-center" aria-label="Memory map">
              <div className="pg-lm-crumbs">
                <nav className="kh-crumbs" aria-label="Memory level">
                  {focus.length ? <button type="button" onClick={goOverall}>Overall memory</button> : <span aria-current="page">Overall memory</span>}
                  {focus.map((id, i) => (
                    <React.Fragment key={id}>
                      <IconChevRight size={12} />
                      {i < focus.length - 1
                        ? <button type="button" onClick={() => { setFocus(focus.slice(0, i + 1)); setSel(null); }}>{byId[id]?.name}</button>
                        : <span aria-current="page">{byId[id]?.name}</span>}
                    </React.Fragment>
                  ))}
                </nav>
                {focus.length > 0 && <button type="button" className="pu-link pg-lm-up" onClick={up}><IconArrowUp size={12} /> Up one level</button>}
              </div>
              {orbit.length ? (
                <MemoryMap skills={orbit} overall={focusNode ? held(focusNode) : overall} centerName={focusNode ? focusNode.name : 'Overall memory'}
                  onSelect={pickOrbit} selected={sel} large hoverSelect={false} sizeBy="mastery" />
              ) : <p className="pu-empty">Nothing below this level yet.</p>}
              <div className="pg-lm-center-foot">
                <Legend />
                <span className="pu-meta">
                  {orbitAll.length > orbit.length ? `Showing the ${orbit.length} most-practised of ${orbitAll.length} — the Topics view lists all. ` : ''}
                  Select a node; select it again to go inside.
                </span>
              </div>
            </section>

            <aside className="pg-lm-col pg-lm-detail" aria-live="polite" aria-label="Details">{detail}</aside>
          </div>
        )}

        {view === 'skills' && (
          <div className="pg-lm-pane">
            <NodeTable list={tree} level="skill" onOpen={into} onDetails={onDetails} onRead={onRead} onPractice={onPractice} />
            <section className="pg-ex-tree">
              <button type="button" className="pu-link" aria-expanded={showTree} onClick={() => setShowTree((o) => !o)}>
                {showTree ? 'Hide' : 'Show'} the full hierarchy <IconChevDown size={13} />
              </button>
              {showTree && (
                <div className="mem-treecard pg-tree">
                  {tree.map((t) => <Row key={t.id} node={t} depth={0} onOpen={onDetails} expanded={expanded} toggle={toggle} />)}
                </div>
              )}
            </section>
          </div>
        )}

        {view === 'topics' && (
          <div className="pg-lm-pane">
            <div className="pg-lm-filters">
              <label className="kh-select">
                <span className="pu-sr">Skill</span>
                <select value={topicSkill} onChange={(e) => setTopicSkill(e.target.value)} aria-label="Filter by skill">
                  <option value="all">All skills</option>
                  {tree.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
                <IconChevDown size={14} />
              </label>
              <div className="pg-seg" role="group" aria-label="Which topics">
                {[['practised', 'Practised'], ['all', 'All topics']].map(([id, l]) => (
                  <button key={id} type="button" aria-pressed={topicScope === id} className={topicScope === id ? 'on' : ''} onClick={() => setTopicScope(id)}>{l}</button>
                ))}
              </div>
              <span className="pu-meta">{topicRows.length} topic{topicRows.length === 1 ? '' : 's'} · most in need first</span>
            </div>
            {topicRows.length
              ? <NodeTable list={topicRows} level="topic" showSkill={topicSkill === 'all'} onOpen={into} onDetails={onDetails} onRead={onRead} onPractice={onPractice} />
              : <p className="pu-empty">No practised topics here yet.</p>}
          </div>
        )}

        {view === 'gaps' && (
          <div className="pg-lm-pane">
            <h3 className="pg-lm-h">Needs attention</h3>
            {gaps.length ? (
              <ul className="pg-lm-gaps">
                {gaps.map((n) => {
                  const why = whyWeak(n);
                  return (
                    <li key={n.id} className={`b-${band(n.mastery).key}`}>
                      <span className="pg-lm-gap-pct"><b>{pct(n.mastery)}</b><small>memory strength</small></span>
                      <span className="pg-lm-gap-main">
                        <button type="button" className="pg-topic-name" onClick={() => into(n)}>{n.name}</button>
                        <small>{trail(n.id)}</small>
                        <span className="pg-lm-gap-why">{why.slice(0, 2).join(' · ')}</span>
                      </span>
                      <span className="pg-lm-gap-when">{ago(n.last_seen_days)}</span>
                      <button type="button" className="pu-btn pu-btn-sm pu-btn-quiet" onClick={() => onPractice(n)}>Practice <IconArrowRight size={12} /></button>
                    </li>
                  );
                })}
              </ul>
            ) : <p className="pu-empty">No weak or fading topics right now.</p>}
            {due.length > 0 && (
              <section className="pg-ex-due" aria-labelledby="pg-ex-due">
                <h3 id="pg-ex-due" className="pg-lm-h">Due for review</h3>
                <ul className="pg-recent">
                  {due.map((d) => (
                    <li key={d.concept_id}>
                      <button type="button" onClick={() => onRead({ id: d.concept_id })}>
                        <span className="pg-recent-main"><b>{nameOf(d.concept_id)}</b><small>{trail(d.concept_id)}</small></span>
                        <span className="pg-recent-score">{pct(d.mastery)}</span>
                        <span className="pg-recent-when">{ago(d.last_seen_days)}</span>
                        <IconChevRight size={13} />
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </div>
        )}

        {view === 'recs' && (
          <div className="pg-lm-pane">
            {strongest || weakest || nextUp ? (
              <div className="pg-lm-recs">
                {strongest && (
                  <article className={`b-${band(strongest.mastery).key}`}>
                    <span className="pg-lm-kicker">Strongest area</span>
                    <h3>{strongest.name} <em>{pct(strongest.mastery)}</em></h3>
                    <p>{strongest.mastery >= 0.6 ? 'Keep practising the advanced concepts to lock it in.' : 'Your best-held topic so far — a short review keeps it from fading.'}</p>
                    <div className="pg-lm-acts">
                      <button type="button" className="pu-btn pu-btn-sm pu-btn-quiet" onClick={() => onPractice(strongest)}>Practice {strongest.name}</button>
                      <button type="button" className="pu-link" onClick={() => into(strongest)}>See in memory</button>
                    </div>
                  </article>
                )}
                {weakest && (
                  <article className={`b-${band(weakest.mastery).key}`}>
                    <span className="pg-lm-kicker">Needs attention</span>
                    <h3>{weakest.name} <em>{pct(weakest.mastery)}</em></h3>
                    <p>{focusOn.length ? `Focus on ${focusOn.map((c) => c.name).join(' and ')}.` : 'Rebuild the basics with a short practice round.'} {whyWeak(weakest)[0]}.</p>
                    <div className="pg-lm-acts">
                      <button type="button" className="pu-btn pu-btn-sm pu-btn-primary" onClick={() => onPractice(weakest)}>Practice {weakest.name}</button>
                      <button type="button" className="pu-link" onClick={() => into(weakest)}>See in memory</button>
                    </div>
                  </article>
                )}
                {nextUp && (
                  <article>
                    <span className="pg-lm-kicker">Next recommended topic</span>
                    <h3>{nextUp.name}{byId[nextUp.id]?.attempts ? <em>{pct(byId[nextUp.id].mastery)}</em> : null}</h3>
                    <p>{weakest && focusOn[0] ? `The weakest part of ${weakest.name}${byId[nextUp.id]?.attempts ? '' : ' — not practised yet'}.` : 'Due for review — it’s starting to fade.'}</p>
                    <div className="pg-lm-acts">
                      <button type="button" className="pu-btn pu-btn-sm pu-btn-quiet" onClick={() => onPractice(byId[nextUp.id] || nextUp)}>Practice {nextUp.name}</button>
                      <button type="button" className="pu-link" onClick={() => onRead(nextUp)}>Read</button>
                    </div>
                  </article>
                )}
              </div>
            ) : <p className="pu-empty">Practise a few topics and recommendations appear here.</p>}
            {recs.length > 0 && (
              <section className="pg-lm-more">
                <h3 className="pg-lm-h">Also for you</h3>
                <ul className="pg-recent">
                  {recs.map((r) => (
                    <li key={r.key}>
                      <button type="button" onClick={r.run}>
                        <span className="pg-recent-main"><b>{r.title}</b><small>{r.kind} · {r.meta}</small></span>
                        <IconChevRight size={13} />
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

/* ══ Progress report building blocks ═══════════════════════════════════════ */
const RANGES = [['week', 'Week', 7], ['month', 'Month', 30], ['quarter', '3 months', 91], ['year', 'Year', 364]];
const TREND_METRICS = [['questions', 'Questions completed'], ['accuracy', 'Accuracy'], ['days', 'Active days'], ['time', 'Learning time']];
const SKIP_EVENTS = new Set(['selector.pick', 'theory.asked', 'session.planned', 'dsa.recommendation']);
const EVENT_MODE = { mcq: 'MCQ', theory: 'Theory', flashcards: 'Flashcards', rapidfire: 'AI Rapid Fire', voice: 'Interviews', dsa: 'Coding', session: 'Sessions' };
const prettyDay = (d) => (d === 0 ? 'Today' : d === 1 ? 'Yesterday' : new Date(Date.now() - d * 86400000).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' }));
const delta = (v, unit = '') => (v == null || v === 0 ? null : <em className={`pr-delta ${v > 0 ? 'up' : 'down'}`}>{v > 0 ? '+' : '−'}{Math.abs(v)}{unit}</em>);

/** Weekly bars (oldest → this week). */
const WeekBars = ({ rows, format = (v) => v, max }) => {
  const top = max || Math.max(1, ...rows.map((r) => r.value || 0));
  return (
    <div className="pr-bars" role="img" aria-label={rows.map((r) => `${r.label}: ${r.value == null ? 'no data' : format(r.value)}`).join(', ')}>
      {rows.map((r) => (
        <div key={r.label} className="pr-bar-col">
          <span className="pr-bar-v">{r.value == null ? '—' : format(r.value)}</span>
          <span className="pr-bar-track"><i style={{ height: `${r.value == null ? 0 : Math.max(4, (r.value / top) * 100)}%` }} /></span>
          <small>{r.label}</small>
        </div>
      ))}
    </div>
  );
};

/** Calendar heatmap of practice days (columns = weeks, rows = weekdays). */
const Heatmap = ({ days, span, selected, onPick }) => {
  const today = (new Date().getDay() + 6) % 7;              // Mon = 0
  const weeks = Math.ceil((span + today + 1) / 7);
  const cells = [];
  for (let w = weeks - 1; w >= 0; w -= 1) {
    for (let wd = 0; wd < 7; wd += 1) {
      const d = w * 7 + (today - wd);
      cells.push({ key: `${w}-${wd}`, d, out: d < 0 || d > span - 1 });
    }
  }
  return (
    <div className="pr-heat" style={{ gridTemplateColumns: `repeat(${weeks}, minmax(6px, 18px))` }}>
      {cells.map((c) => (
        c.out ? <span key={c.key} className="pr-cell out" aria-hidden="true" /> : (
          <button key={c.key} type="button" className={`pr-cell${days.has(c.d) ? ' on' : ''}${selected === c.d ? ' sel' : ''}${c.d === 0 ? ' today' : ''}`}
            onClick={() => onPick(c.d)} aria-label={`${prettyDay(c.d)}: ${days.has(c.d) ? 'practised' : 'no recorded practice'}`} aria-pressed={selected === c.d} />
        )
      ))}
    </div>
  );
};

/** Radar of the goal role's skills (proficiency 0–100; unassessed sit at 0, dashed). */
const Radar = ({ rows }) => {
  const pts = rows.slice(0, 8);
  if (pts.length < 3) return null;
  const W = 340; const H = 300; const cx = W / 2; const cy = H / 2 + 6; const R = 104;
  const ang = (i) => -Math.PI / 2 + (i / pts.length) * Math.PI * 2;
  const at = (i, r) => [cx + Math.cos(ang(i)) * r, cy + Math.sin(ang(i)) * r];
  const poly = (fn) => pts.map((p, i) => at(i, fn(p)).join(',')).join(' ');
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="pr-radar" role="img"
      aria-label={`Skill proficiency: ${pts.map((p) => `${p.name} ${p.proficiency == null ? 'not enough data' : p.proficiency}`).join(', ')}`}>
      {[0.25, 0.5, 0.75, 1].map((f) => <polygon key={f} points={poly(() => R * f)} className="pr-radar-ring" />)}
      {pts.map((p, i) => { const [x, y] = at(i, R); return <line key={p.id} x1={cx} y1={cy} x2={x} y2={y} className="pr-radar-axis" />; })}
      <polygon points={poly(() => R * 0.75)} className="pr-radar-target" />
      <polygon points={poly((p) => R * ((p.proficiency || 0) / 100))} className="pr-radar-you" />
      {pts.map((p, i) => {
        const [x, y] = at(i, R * ((p.proficiency || 0) / 100));
        const [lx, ly] = at(i, R + 18);
        return (
          <g key={p.id}>
            <circle cx={x} cy={y} r="3.5" className={`pr-radar-dot s-${p.status}`} />
            <text x={lx} y={ly} textAnchor={Math.abs(lx - cx) < 8 ? 'middle' : lx > cx ? 'start' : 'end'} dominantBaseline="middle" className="pr-radar-label">{p.name.length > 18 ? `${p.name.slice(0, 17)}…` : p.name}</text>
          </g>
        );
      })}
    </svg>
  );
};


/* ── Growth levels: where each skill sits (from proficiency + evidence) ── */
const GROWTH = [
  { id: 'expert', label: 'Expert', test: (r) => r.proficiency != null && r.proficiency >= 80 },
  { id: 'proficient', label: 'Proficient', test: (r) => r.proficiency != null && r.proficiency >= 65 },
  { id: 'competent', label: 'Competent', test: (r) => r.proficiency != null && r.proficiency >= 50 },
  { id: 'beginner', label: 'Advanced beginner', test: (r) => r.proficiency != null },
  { id: 'novice', label: 'Novice', test: (r) => r.attempts > 0 },
  { id: 'none', label: 'Not started', test: () => true },
];
const growthOf = (r) => GROWTH.find((g) => g.test(r)).id;

const GrowthRing = ({ counts, total }) => {
  const R = 52; const C = 2 * Math.PI * R; const gap = 4;
  let off = 0;
  const segs = GROWTH.filter((g) => counts[g.id]);
  return (
    <svg viewBox="0 0 140 140" className="pr-grow-ring" role="img" aria-label={segs.map((g) => `${g.label} ${counts[g.id]}`).join(', ')}>
      <circle cx="70" cy="70" r={R} className="pr-grow-track" />
      {segs.map((g) => {
        const len = (counts[g.id] / total) * C;
        const el = <circle key={g.id} cx="70" cy="70" r={R} className={`pr-grow-seg g-${g.id}`} strokeDasharray={`${Math.max(0, len - (segs.length > 1 ? gap : 0))} ${C}`} strokeDashoffset={-off} />;
        off += len;
        return el;
      })}
    </svg>
  );
};

/* ── Skills → roles flow (Sankey): your practice on the left, the job titles it builds toward on the right ── */
const FLOW_HUES = ['#8f86c9', '#d6a24a', '#7fae8e', '#cf8a8a', '#a3adbb'];
const SkillFlow = ({ skills, roles, onSkill }) => {
  // Compact: ~150px of nodes; every node keeps a minimum height so one-line labels never collide.
  const W = 640; const pad = 6; const gap = 8; const nodeW = 8; const lx = 200; const rx = W - 200; const AREA = 150; const MIN_H = 18;
  const total = skills.reduce((x, k) => x + k.value, 0) || 1;
  const place = (list) => {
    const sum = list.reduce((x, n) => x + n.value, 0) || 1;
    let y = pad;
    return list.map((n) => { const h = Math.max(MIN_H, (n.value / sum) * AREA); const o = { ...n, y, h, used: 0 }; y += h + gap; return o; });
  };
  const L = place(skills);
  const Rn = place(roles);
  const H = Math.max(...[...L, ...Rn].map((n) => n.y + n.h)) + pad;
  const byRole = Object.fromEntries(Rn.map((r) => [r.id, r]));
  const links = [];
  L.forEach((sk, i) => sk.links.forEach((lk) => {
    const r = byRole[lk.roleId];
    if (!r) return;
    const h1 = (lk.value / (sk.value || 1)) * sk.h; const h2 = (lk.value / (r.value || 1)) * r.h;
    const y1 = sk.y + sk.used; const y2 = r.y + r.used;
    sk.used += h1; r.used += h2;
    const x1 = lx + nodeW; const x2 = rx; const mx = (x1 + x2) / 2;
    links.push({ key: `${sk.id}-${r.id}`, color: FLOW_HUES[sk.other ? 4 : i % 4],
      d: `M${x1},${y1} C${mx},${y1} ${mx},${y2} ${x2},${y2} L${x2},${y2 + h2} C${mx},${y2 + h2} ${mx},${y1 + h1} ${x1},${y1 + h1} Z` });
  }));
  const clip = (t, n) => (t.length > n ? `${t.slice(0, n - 1)}…` : t);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="pr-flow" role="img"
      aria-label={`Practice by skill: ${L.map((x) => `${x.name} ${Math.round((x.value / total) * 100)}%`).join(', ')}. Builds toward: ${Rn.map((r) => r.name).join(', ')}`}>
      {links.map((l) => <path key={l.key} d={l.d} fill={l.color} className="pr-flow-link" />)}
      {L.map((x, i) => (
        <g key={x.id} className={`pr-flow-node${x.other ? ' other' : ''}`} onClick={() => !x.other && onSkill?.(x.id)}
          role={x.other ? undefined : 'button'} tabIndex={x.other ? undefined : 0} onKeyDown={(e) => { if (e.key === 'Enter' && !x.other) onSkill?.(x.id); }}>
          <rect x={lx} y={x.y} width={nodeW} height={x.h} rx="2" fill={FLOW_HUES[x.other ? 4 : i % 4]} />
          <text x={lx - 10} y={x.y + x.h / 2} textAnchor="end" dominantBaseline="middle" className="pr-flow-name">
            <tspan className="pr-flow-pct">{Math.round((x.value / total) * 100)}%</tspan>{'  '}{clip(x.name, 22)}
          </text>
        </g>
      ))}
      {Rn.map((r) => (
        <g key={r.id}>
          <rect x={rx} y={r.y} width={nodeW} height={r.h} rx="2" className={`pr-flow-role${r.other ? ' other' : ''}${r.goal ? ' goal' : ''}`} />
          <text x={rx + nodeW + 10} y={r.y + r.h / 2} dominantBaseline="middle" className={`pr-flow-name${r.goal ? ' strong' : ''}`}>
            {clip(r.name, 22)}<tspan className="pr-flow-sub">{'  '}{Math.round((r.value / total) * 100)}%{r.goal ? ' · goal' : ''}</tspan>
          </text>
        </g>
      ))}
    </svg>
  );
};

const StatusTag = ({ s }) => <span className={`pr-status s-${s}`}>{STATUS_R[s].label}</span>;

const eventLine = (g, nameOf) => {
  const t = g.topic ? ` on ${g.topic}` : '';
  switch (g.type) {
    case 'mcq.answered': return `Answered ${g.n} MCQ${g.n > 1 ? 's' : ''}${t}${g.graded ? ` · ${g.correct}/${g.graded} correct` : ''}`;
    case 'theory.answered': return `Answered ${g.n} theory question${g.n > 1 ? 's' : ''}${t}`;
    case 'theory.skipped': return `Skipped a theory question${t}`;
    case 'theory.hint_used': return `Used a hint${t}`;
    case 'flashcard.feedback': return `Reviewed ${g.n} flashcard${g.n > 1 ? 's' : ''}${t}`;
    case 'rapidfire.answered': return `Rapid fire: ${g.n} question${g.n > 1 ? 's' : ''}${t}${g.graded ? ` · ${g.correct}/${g.graded} correct` : ''}`;
    case 'rapidfire.round': return 'Finished a rapid-fire round';
    case 'voice.interview': return 'Completed an AI interview';
    case 'tutor.session_started': return `Started a theory session${t}`;
    case 'dsa.submit': return 'Submitted a coding solution';
    case 'dsa.solved': return 'Solved a coding problem';
    default: return g.type.replace(/[._]/g, ' ');
  }
};

/** Group recent events into readable activity items. */
const activityItems = (events, nameOf) => {
  const out = [];
  (events || []).forEach((e) => {
    if (SKIP_EVENTS.has(e.type)) return;
    const cid = (e.concept_ids || [])[0];
    const topic = cid ? nameOf(String(cid).split('.').slice(0, 2).join('.')) : '';
    const day = new Date(e.ts).toDateString();
    const last = out[out.length - 1];
    if (last && last.type === e.type && last.topic === topic && last.day === day) {
      last.n += 1; if (e.outcome) { last.graded += 1; if (e.outcome.correct) last.correct += 1; }
      return;
    }
    out.push({ key: `${e.ts}-${out.length}`, type: e.type, surface: e.surface, topic, cid, day, ts: e.ts, n: 1,
      graded: e.outcome ? 1 : 0, correct: e.outcome?.correct ? 1 : 0 });
  });
  return out.map((g) => ({ ...g, text: eventLine(g, nameOf), mode: EVENT_MODE[g.surface] || 'Other' }));
};

/* ══ The Progress report ════════════════════════════════════════════════════ */
export default function MemoryPage({
  onPractice, onStartPractice, onOpenHubNode, onOpenHub, onOpenPath, onOpenInterviews, onStartInterview,
  profile, onEditProfile, theme, onToggleTheme,
}) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [snap, setSnap] = useState(null);
  const [hub, setHub] = useState(null);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState(() => new Set());
  const [open, setOpen] = useState(null);        // concept/topic drawer (depth ladder, gaps)
  const [explore, setExplore] = useState(null);  // the Learning Memory overlay
  const [chooser, setChooser] = useState(null);
  const [range, setRange] = useState('month');
  const [metric, setMetric] = useState('questions');
  const [pickedDay, setPickedDay] = useState(0);
  const [sheet, setSheet] = useState(null);      // { skillId, topicId? } — skill / topic detail
  const [allSkills, setAllSkills] = useState(false);
  const [activityAll, setActivityAll] = useState(false);
  const [actMode, setActMode] = useState('All');
  const [actSkill, setActSkill] = useState('');
  const [prefs, setPrefs] = useState(loadPrefs);

  const load = useCallback(async () => {
    setError('');
    try {
      const d = await APIService.getMemoryTree();
      setData(d);
      setExpanded(new Set((d.tree || []).filter((t) => t.attempts > 0).map((t) => t.id)));
    } catch (e) {
      setError(e.message || 'Could not load your progress.');
    }
  }, []);
  useEffect(() => {
    load();
    APIService.getEngineMemory().then(setSnap).catch(() => setSnap({}));
    getHubTreeCached().then((d) => setHub(d.tree || [])).catch(() => setHub([]));
  }, [load]);

  const toggle = (id) => setExpanded((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const practiceDraft = (node) => { const draft = sessionStore.loadDraft() || {}; sessionStore.saveDraft({ ...draft, sel: [node.id] }); onPractice?.(); };
  const practiceWith = (node, mode = 'mcq') => setChooser({ subjects: [{ id: node.id, name: node.name, kind: 'topic' }], title: node.name, mode });
  const harder = (node) => onStartPractice?.({ mode: 'mcq', subjects: [{ id: node.id, name: node.name, kind: 'topic' }], size: 5, difficulty: 'hard' });
  const setPref = (patch) => setPrefs((p) => { const n = { ...p, ...patch }; savePrefs(n); return n; });

  const tree = useMemo(() => data?.tree || [], [data]);
  const memIndex = useMemo(() => indexTree(tree), [tree]);
  const paths = useMemo(() => (hub ? buildPaths(hub) : null), [hub]);
  const searchEntries = useMemo(() => buildSearchEntries(hub, paths ? [...paths.roles, ...paths.areas] : []), [hub, paths]);
  const events = useMemo(() => snap?.recent_events || [], [snap]);
  const due = useMemo(() => snap?.due_reviews || [], [snap]);
  const trend = useMemo(() => [...(data?.trend || [])].sort((a, b) => b.weeks_ago - a.weeks_ago), [data]);   // oldest → newest
  const nameOf = (id) => memIndex[id]?.name || String(id).split('.').pop().replace(/_/g, ' ');
  const trail = (id) => {
    const parts = String(id).split('.');
    const names = [];
    for (let i = 1; i < parts.length; i += 1) { const n = memIndex[parts.slice(0, i).join('.')]; if (n) names.push(n.name); }
    return names.join(' › ');
  };

  /* ── goal, role, history ── */
  const goalId = loadGoal();
  const goalPath = paths ? [...paths.roles, ...paths.areas].find((p) => p.id === goalId) : null;
  const role = roleForGoal(goalId);
  const history = useMemo(() => loadHistory(user?.uid), [user]);
  const interviews = history.filter((e) => e.kind !== 'rapidfire');

  /* ── overview ── */
  const totals = totalsOf(snap);
  const days = useMemo(() => practiceDays(tree, events), [tree, events]);
  const streak = streakFrom(days);
  const longest = longestRun(days);
  const weeksActive = new Set([...days].map((d) => Math.floor(d / 7))).size;
  const thisWk = trend.find((w) => w.weeks_ago === 0);
  const lastWk = trend.find((w) => w.weeks_ago === 1);
  const daysIn = (from, to) => [...days].filter((d) => d >= from && d <= to).length;
  const today = (new Date().getDay() + 6) % 7;
  const daysThisWeek = daysIn(0, today);
  const daysLastWeek = daysIn(today + 1, today + 7);
  const cmp = {
    questions: thisWk && lastWk && lastWk.attempts ? (thisWk.attempts || 0) - lastWk.attempts : null,
    accuracy: thisWk?.accuracy != null && lastWk?.accuracy != null ? Math.round((thisWk.accuracy - lastWk.accuracy) * 100) : null,
    days: daysThisWeek - daysLastWeek,
  };

  /* ── activity ── */
  const span = (RANGES.find((r) => r[0] === range) || RANGES[1])[2];
  const activeInRange = [...days].filter((d) => d < span).length;
  const weekRows = trend.map((w) => ({
    label: w.weeks_ago === 0 ? 'This wk' : w.weeks_ago === 1 ? 'Last wk' : `${w.weeks_ago} wks ago`,
    value: metric === 'questions' ? (w.attempts || 0) : metric === 'accuracy' ? (w.accuracy == null ? null : Math.round(w.accuracy * 100))
      : (w.weeks_ago === 0 ? daysIn(0, today) : daysIn(today + 1 + (w.weeks_ago - 1) * 7, today + w.weeks_ago * 7)),
  }));
  const day = dayDetail(pickedDay, { events, memTree: tree });

  /* ── skills ── */
  const rows = useMemo(() => skillRows({ role, hub, memTree: tree }), [role, hub, tree]);
  const allRows = useMemo(() => skillRows({ role: null, hub, memTree: tree }).filter((r) => r.attempts > 0), [hub, tree]);
  const skillList = (role ? rows : allRows).slice().sort((a, b) => (b.attempts > 0) - (a.attempts > 0) || (b.proficiency ?? -1) - (a.proficiency ?? -1));
  const coverage = coverageOf(rows);
  const readiness = role ? readinessOf({ rows, interviews: interviews.filter((e) => e.setup?.roleId === role.id) }) : null;
  const overall = useMemo(() => {
    const p = tree.filter((t) => t.attempts > 0); const n = p.reduce((s, t) => s + t.attempts, 0);
    return n ? p.reduce((s, t) => s + (t.mastery || 0) * t.attempts, 0) / n : null;
  }, [tree]);

  /* ── active practice: skills practised in the last 30 days ── */
  const activeSkills = useMemo(() => {
    const modesBy = {};
    events.forEach((e) => (e.concept_ids || []).forEach((cid) => {
      const k = String(cid).split('.').slice(0, 2).join('.');
      if (EVENT_MODE[e.surface] && !SKIP_EVENTS.has(e.type)) (modesBy[k] = modesBy[k] || new Set()).add(EVENT_MODE[e.surface]);
    }));
    return tree.flatMap((r) => (r.children || []).map((s) => ({ ...s, root: r })))
      .filter((s) => s.attempts > 0 && (s.last_seen_days ?? 99) <= 30)
      .sort((a, b) => (a.last_seen_days ?? 99) - (b.last_seen_days ?? 99) || b.attempts - a.attempts)
      .slice(0, 5)
      .map((s) => {
        const lv = leavesOfNode(s); const done = lv.filter((l) => l.attempts > 0).length;
        return { ...s, coverage: lv.length ? done / lv.length : 0, modes: [...(modesBy[s.id] || [])] };
      });
  }, [tree, events]);

  /* ── growth status across the skill map ── */
  const growthBase = useMemo(() => (role ? rows : skillRows({ role: null, hub, memTree: tree }).filter((r) => tree.some((t) => t.attempts > 0 && r.competency === t.id))), [role, rows, hub, tree]);
  const growth = useMemo(() => {
    const c = {}; growthBase.forEach((r) => { const g = growthOf(r); c[g] = (c[g] || 0) + 1; });
    return c;
  }, [growthBase]);
  const started = growthBase.filter((r) => r.attempts > 0).length;

  /* ── skills → roles flow (top 4 of each; the rest fold into "Other") ── */
  const flow = useMemo(() => {
    const live = ROLES.filter((r) => r.rootIds.length);
    const leads = (skillId, rl) => rl.rootIds.some((id) => skillId === id || skillId.startsWith(`${id}.`) || id.startsWith(`${skillId}.`));
    const all = allRows.filter((r) => r.attempts > 0)
      .map((r) => ({ id: r.id, name: r.name, value: r.attempts, roles: live.filter((rl) => leads(r.id, rl)) }))
      .filter((x) => x.roles.length)
      .sort((a, b) => b.value - a.value);
    const roleVal = {};
    all.forEach((x) => x.roles.forEach((rl) => { roleVal[rl.id] = (roleVal[rl.id] || 0) + x.value / x.roles.length; }));
    const rankedRoles = Object.entries(roleVal).sort((a, b) => b[1] - a[1]).map(([id]) => id);
    const topRoles = rankedRoles.slice(0, 4);
    if (role && rankedRoles.includes(role.id) && !topRoles.includes(role.id)) topRoles[3] = role.id;   // the goal always shows
    const roleKey = (id) => (topRoles.includes(id) ? id : 'other-roles');
    const linksOf = (x) => {
      const m = {};
      x.roles.forEach((rl) => { const k = roleKey(rl.id); m[k] = (m[k] || 0) + x.value / x.roles.length; });
      return Object.entries(m).map(([roleId, value]) => ({ roleId, value }));
    };
    const top = all.slice(0, 4).map((x) => ({ ...x, links: linksOf(x) }));
    const rest = all.slice(4);
    const skills = [...top];
    if (rest.length) {
      const m = {};
      rest.forEach((x) => linksOf(x).forEach((l) => { m[l.roleId] = (m[l.roleId] || 0) + l.value; }));
      skills.push({ id: 'other-skills', name: `${rest.length} other skill${rest.length === 1 ? '' : 's'}`, other: true,
        value: rest.reduce((a, x) => a + x.value, 0), links: Object.entries(m).map(([roleId, value]) => ({ roleId, value })) });
    }
    const rv = {};
    skills.forEach((x) => x.links.forEach((l) => { rv[l.roleId] = (rv[l.roleId] || 0) + l.value; }));
    const roles = topRoles.filter((id) => rv[id]).map((id) => ({ id, name: live.find((x) => x.id === id).title, value: rv[id], goal: role?.id === id }));
    const otherN = rankedRoles.length - roles.length;
    if (rv['other-roles']) roles.push({ id: 'other-roles', name: `${otherN} other role${otherN === 1 ? '' : 's'}`, value: rv['other-roles'], other: true });
    return { skills, roles, answers: all.reduce((a, x) => a + x.value, 0), skillCount: all.length, roleCount: rankedRoles.length };
  }, [allRows, role]);

  /* ── memory, strengths, practice ── */
  const strengths = useMemo(() => strengthsOf(tree), [tree]);
  const improve = useMemo(() => improvementsOf(tree, due), [tree, due]);
  const diff = useMemo(() => byDifficulty(events), [events]);
  const bs = snap?.by_surface || {};
  const bt = snap?.by_type || {};
  const modes = [
    { mode: 'MCQ', n: bs.mcq?.attempts, metric: bs.mcq?.attempts ? `${pct(bs.mcq.correct / bs.mcq.attempts)} accuracy` : null, v: bs.mcq?.attempts ? bs.mcq.correct / bs.mcq.attempts : null, unit: 'questions' },
    { mode: 'Theory', n: bt['theory.answered'], metric: bs.theory?.attempts ? `${pct(bs.theory.correct / bs.theory.attempts)} graded as solid` : null, v: bs.theory?.attempts ? bs.theory.correct / bs.theory.attempts : null, unit: 'answers' },
    { mode: 'Flashcards', n: bt['flashcard.feedback'], metric: bs.flashcards?.attempts ? `${pct(bs.flashcards.correct / bs.flashcards.attempts)} recalled (self-rated)` : null, v: bs.flashcards?.attempts ? bs.flashcards.correct / bs.flashcards.attempts : null, unit: 'cards reviewed' },
    { mode: 'AI Rapid Fire', n: bs.rapidfire?.attempts, metric: bs.rapidfire?.attempts ? `${pct(bs.rapidfire.correct / bs.rapidfire.attempts)} correct` : null, v: bs.rapidfire?.attempts ? bs.rapidfire.correct / bs.rapidfire.attempts : null, unit: 'questions' },
    { mode: 'Coding', n: bt['dsa.submit'], metric: bt['dsa.solved'] ? `${bt['dsa.solved']} solved` : null, v: bt['dsa.submit'] ? (bt['dsa.solved'] || 0) / bt['dsa.submit'] : null, unit: 'submissions' },
    { mode: 'Interviews', n: interviews.length, metric: interviews.length ? `${interviews.length} with feedback` : null, v: null, unit: 'completed' },
  ].filter((m) => m.n > 0);
  const gaps = [...days].sort((a, b) => a - b);
  let longestBreak = 0; for (let i = 1; i < gaps.length; i += 1) longestBreak = Math.max(longestBreak, gaps[i] - gaps[i - 1] - 1);

  /* ── interviews ── */
  const avgOf = (list) => { const s = list.filter((e) => e.score != null); return s.length ? Math.round(s.reduce((a, e) => a + e.score, 0) / s.length) : null; };
  const aiIv = interviews.filter((e) => e.kind !== 'written');
  const wrIv = interviews.filter((e) => e.kind === 'written');
  const recurring = recurringInterviewGap(interviews);

  /* ── insights + next actions (what happened · why it matters · what to do) ── */
  const insights = useMemo(() => {
    const out = [];
    if (cmp.questions != null) out.push({ key: 'q', tone: cmp.questions >= 0 ? 'up' : 'down', title: cmp.questions >= 0 ? `You completed ${cmp.questions} more questions than last week` : `You completed ${Math.abs(cmp.questions)} fewer questions than last week`, why: 'Regular practice is what turns answers into memory.', action: null });
    if (cmp.accuracy != null && Math.abs(cmp.accuracy) >= 5) out.push({ key: 'a', tone: cmp.accuracy > 0 ? 'up' : 'down', title: `Accuracy ${cmp.accuracy > 0 ? 'improved' : 'dropped'} by ${Math.abs(cmp.accuracy)} points this week`, why: cmp.accuracy > 0 ? 'Your recent answers are landing more often.' : 'Recent questions may be harder, or topics need a refresh.', action: null });
    if (improve[0]) out.push({ key: 'w', tone: 'down', title: `${improve[0].node.name}: ${improve[0].gaps[0].type.toLowerCase()}`, why: improve[0].gaps[0].why, action: { label: `Practice ${improve[0].node.name}`, run: () => practiceWith(improve[0].node) } });
    if (due.length) out.push({ key: 'd', tone: 'neutral', title: `${due.length} concept${due.length === 1 ? ' is' : 's are'} ready for revision`, why: 'They haven’t been practised recently, so a quick recall check helps them stick.', action: { label: 'Review flashcards', run: () => practiceWith({ id: due[0].concept_id.split('.').slice(0, 2).join('.'), name: nameOf(due[0].concept_id.split('.').slice(0, 2).join('.')) }, 'flashcards') } });
    if (role && coverage.notAssessed > 0) out.push({ key: 'c', tone: 'neutral', title: `${coverage.notAssessed} of ${coverage.total} ${role.title} skills aren’t assessed yet`, why: 'Readiness can only reflect what you’ve practised.', action: null });
    if (recurring) out.push({ key: 'r', tone: 'down', title: `${recurring.topic} came up as a gap in ${recurring.times} interviews`, why: 'A recurring interview gap is the clearest signal of what to strengthen next.', action: null });
    return out.slice(0, 4);
  }, [cmp.questions, cmp.accuracy, improve, due, role, coverage.notAssessed, coverage.total, recurring]);   // eslint-disable-line react-hooks/exhaustive-deps

  const nextActions = useMemo(() => {
    const out = [];
    if (improve[0]) out.push({ key: 'i', title: `Strengthen ${improve[0].node.name}`, why: improve[0].gaps[0].why, acts: [['Review concepts', () => onOpenHubNode?.(improve[0].node.id)], ['Practice questions', () => practiceWith(improve[0].node)]] });
    if (due[0]) { const t = due[0].concept_id; out.push({ key: 'd', title: `Revisit ${nameOf(t)}`, why: `You haven’t practised this in ${Math.round(due[0].last_seen_days)} days.`, acts: [['Review flashcards', () => practiceWith({ id: t, name: nameOf(t) }, 'flashcards')]] }); }
    if (strengths[0]) out.push({ key: 's', title: `Try harder ${strengths[0].node.name} questions`, why: `${strengths[0].evidence[0]} — test whether it holds at a harder level.`, acts: [['Start practice', () => harder(strengths[0].node)]] });
    if (role) out.push({ key: 'iv', title: `Take a ${role.title} AI interview`, why: 'Check whether your practice translates into interview answers.', acts: [['Start interview', () => onStartInterview?.()]] });
    return out.slice(0, 3);
  }, [improve, due, strengths, role]);   // eslint-disable-line react-hooks/exhaustive-deps

  /* ── recent activity ── */
  const activity = useMemo(() => activityItems(events, nameOf), [events, memIndex]);   // eslint-disable-line react-hooks/exhaustive-deps
  const actModes = ['All', ...new Set(activity.map((a) => a.mode))];
  const actSkills = [...new Set(activity.map((a) => a.topic).filter(Boolean))];
  const actShown = activity.filter((a) => (actMode === 'All' || a.mode === actMode) && (!actSkill || a.topic === actSkill));

  const downloadCsv = () => {
    const lines = [['Section', 'Name', 'Value', 'Detail']];
    lines.push(['Overview', 'Days practised', days.size, 'lower bound'], ['Overview', 'Weeks active', weeksActive, ''], ['Overview', 'Questions attempted', totals.attempted, ''],
      ['Overview', 'Questions completed', totals.completed, ''], ['Overview', 'Accuracy', totals.accuracy == null ? '' : pct(totals.accuracy), 'graded answers'], ['Overview', 'Current streak', streak, 'days'], ['Overview', 'Longest streak', longest, 'days']);
    skillList.forEach((r) => lines.push(['Skill', r.name, r.proficiency ?? '', `${STATUS_R[r.status].label} · ${r.attempts} answers · ${r.accuracy == null ? '' : pct(r.accuracy)}`]));
    if (readiness?.score != null) lines.push(['Readiness', role.title, readiness.score, `${coverage.assessed}/${coverage.total} skills assessed`]);
    interviews.forEach((e) => lines.push(['Interview', e.title, e.score ?? '', `${typeLabel(e.kind)} · ${new Date(e.ts).toLocaleDateString()}`]));
    const csv = lines.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a'); a.href = url; a.download = 'prepare-up-progress.csv'; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  };

  const displayName = profile?.display_name || profile?.name || '';
  // Overlays render outside .pu-page: its container-query containment would make
  // position:fixed panels anchor to the page top instead of the viewport.
  const shell = (body, overlays = null) => (
    <div className="pu-home pu-hub pu-report pu-pr">
      <div className="pu-page">
        <header className="pu-topbar">
          <HomeSearch entries={searchEntries} onPick={(e) => (e.kind === 'track' ? onOpenPath?.(e.id) : onOpenHubNode?.(e.id))} />
          <div className="pu-topbar-end">
            {onToggleTheme && <ThemeToggle theme={theme} onToggle={onToggleTheme} />}
            <button type="button" className="pu-avatar" onClick={onEditProfile} aria-label={displayName ? `${displayName} — profile` : 'Profile'}>
              {profile?.avatar ? <img src={profile.avatar} alt="" /> : <span>{(displayName || 'U').slice(0, 1).toUpperCase()}</span>}
            </button>
          </div>
        </header>
        {body}
      </div>
      {explore && (
        <MemoryExplorer tree={tree} overall={overall} due={due.slice(0, 6)} nameOf={nameOf} trail={trail}
          recs={[]} initial={explore} expanded={expanded} toggle={toggle}
          onClose={() => setExplore(null)} onPractice={(n) => practiceWith(n)}
          onRead={(n) => { setExplore(null); onOpenHubNode?.(n.id); }} onDetails={setOpen} />
      )}
      {overlays}
      {chooser && (
        <PracticeChooser title={chooser.title} subjects={chooser.subjects} initialMode={chooser.mode}
          onStart={(o) => { setChooser(null); setExplore(null); setSheet(null); onStartPractice?.(o); }} onClose={() => setChooser(null)} />
      )}
    </div>
  );

  const head = (
    <section className="pr-head">
      <div>
        <h1>My learning</h1>
        <p>What you’ve learned, how well it’s holding, and what to do next.</p>
      </div>
      <button type="button" className="pu-btn pu-btn-sm pu-btn-quiet" onClick={downloadCsv} disabled={!data}>Download report (CSV)</button>
    </section>
  );
  if (error) return shell(<>{head}<p className="pu-empty">{error} <button type="button" className="pu-link" onClick={load}>Try again</button></p></>);
  if (!data || !snap || !hub) return shell(<>{head}<div className="pr-skel" aria-busy="true"><i /><i /><i /><i /></div></>);

  const sheetSkill = sheet ? memIndex[sheet.skillId] : null;
  const sheetTopic = sheet?.topicId ? memIndex[sheet.topicId] : null;
  const sheetSkillRow = sheetSkill ? (rows.concat(allRows)).find((r) => r.id === sheetSkill.id) : null;
  const ring = readiness?.score ?? null;
  const C = 2 * Math.PI * 40;

  return shell(
    <>
      {head}

      {/* 1 · Learning overview */}
      <div className="pr-top">
        <section className="pr-hero" aria-label="Your readiness">
          <div className="pr-hero-who">
            {profile?.avatar ? <img src={profile.avatar} alt="" /> : <span>{(displayName || 'You').slice(0, 1).toUpperCase()}</span>}
            <div><b>{displayName || 'Your learning'}</b><small>{role ? `Preparing for ${role.title}` : goalPath ? `Goal: ${goalPath.title}` : 'No goal set yet'}</small></div>
          </div>
          <button type="button" className="pr-hero-ring" onClick={() => document.getElementById('pr-ready')?.scrollIntoView({ behavior: 'smooth' })} aria-label="See interview readiness">
            <svg viewBox="0 0 100 100" aria-hidden="true">
              <circle cx="50" cy="50" r="40" className="pr-ring-track" />
              {ring != null && <circle cx="50" cy="50" r="40" className="pr-ring-val" strokeDasharray={`${(ring / 100) * C} ${C}`} />}
            </svg>
            <span>{ring != null ? <><b>{ring}</b><small>/100 ready</small></> : <small>{role ? 'Readiness in progress' : 'Set a goal'}</small>}</span>
          </button>
          <dl className="pr-hero-stats">
            <div><dt>Accuracy</dt><dd>{totals.accuracy == null ? '—' : pct(totals.accuracy)}</dd></div>
            <div><dt>Skills assessed</dt><dd>{role ? `${coverage.assessed}/${coverage.total}` : allRows.filter((r) => r.proficiency != null).length}</dd></div>
            <div><dt>Streak</dt><dd>{streak}d</dd></div>
          </dl>
        </section>

        <section className="pu-panel pr-overview" aria-labelledby="pr-ov">
          <div className="pr-sec-head"><h2 id="pr-ov">Learning overview</h2><span className="pr-sub">All time · compared with last week</span></div>
          <dl className="pr-stats">
            <div><dt>Learning time</dt><dd className="muted">Not tracked yet</dd></div>
            <div><dt>Days practised</dt><dd>{days.size}{delta(cmp.days, ' this wk')}</dd></div>
            <div><dt>Weeks active</dt><dd>{weeksActive}</dd></div>
            <div><dt>Questions attempted</dt><dd>{num(totals.attempted)}</dd></div>
            <div><dt>Questions completed</dt><dd>{num(totals.completed)}{delta(cmp.questions, ' this wk')}</dd></div>
            <div><dt>Accuracy</dt><dd>{totals.accuracy == null ? '—' : pct(totals.accuracy)}{delta(cmp.accuracy, ' pts')}</dd></div>
            <div><dt>Current streak</dt><dd>{streak} {streak === 1 ? 'day' : 'days'}</dd></div>
            <div><dt>Longest streak</dt><dd>{longest} {longest === 1 ? 'day' : 'days'}</dd></div>
          </dl>
          <p className="pr-foot">Accuracy counts graded answers (MCQ, theory, rapid fire, coding); flashcard self-ratings count as recall. Days are a lower bound — older activity is summarised per topic.</p>
        </section>
      </div>

      {/* 2 · Activity */}
      <section className="pu-panel pr-activity" aria-labelledby="pr-act">
        <div className="pr-sec-head">
          <h2 id="pr-act">Learning activity</h2>
          <div className="pr-seg" role="group" aria-label="Range">
            {RANGES.map(([id, l]) => <button key={id} type="button" className={range === id ? 'on' : ''} aria-pressed={range === id} onClick={() => setRange(id)}>{l}</button>)}
          </div>
        </div>
        <div className="pr-act-grid">
          <div>
            <p className="pr-sub">{activeInRange} active day{activeInRange === 1 ? '' : 's'} in the last {span === 7 ? 'week' : span === 30 ? 'month' : span === 91 ? '3 months' : 'year'} · select a day</p>
            <Heatmap days={days} span={span} selected={pickedDay} onPick={setPickedDay} />
            <div className="pr-day" aria-live="polite">
              <b>{prettyDay(pickedDay)}</b>
              {days.has(pickedDay) ? (
                <ul>
                  {day.graded > 0 && <li>{day.graded} graded answer{day.graded === 1 ? '' : 's'} · {day.correct} correct</li>}
                  {day.sessions > 0 && <li>{day.sessions} session{day.sessions === 1 ? '' : 's'} started</li>}
                  {day.topics.length > 0 && <li>Practised {day.topics.slice(0, 4).join(', ')}{day.topics.length > 4 ? ` +${day.topics.length - 4}` : ''}</li>}
                  {!day.graded && !day.sessions && !day.topics.length && <li>Practice recorded — details are only kept for recent activity.</li>}
                </ul>
              ) : <span className="pr-sub">No practice recorded.</span>}
            </div>
          </div>
          <div>
            <div className="pr-metric-pick" role="group" aria-label="Metric">
              {TREND_METRICS.map(([id, l]) => (
                <button key={id} type="button" disabled={id === 'time'} title={id === 'time' ? 'Learning time isn’t tracked yet' : undefined}
                  className={metric === id ? 'on' : ''} aria-pressed={metric === id} onClick={() => setMetric(id)}>{l}</button>
              ))}
            </div>
            <WeekBars rows={weekRows} format={(v) => (metric === 'accuracy' ? `${v}%` : v)} max={metric === 'accuracy' ? 100 : metric === 'days' ? 7 : undefined} />
            <p className="pr-foot">Weekly totals cover the last 4 weeks.</p>
          </div>
        </div>
      </section>

      {/* Active practice + growth status */}
      <div className="pr-row">
        <section className="pu-panel pr-active" aria-labelledby="pr-ap">
          <div className="pr-sec-head"><div><h2 id="pr-ap">Active practice</h2><span className="pr-sub">Skills you’ve practised in the last 30 days</span></div></div>
          {activeSkills.length ? (
            <table className="pr-table pr-active-t">
              <thead><tr><th>Skill</th><th>Modes</th><th>Coverage</th><th>Last practised</th><th>Accuracy</th></tr></thead>
              <tbody>
                {activeSkills.map((s) => (
                  <tr key={s.id} onClick={() => setSheet({ skillId: s.id })} tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter') setSheet({ skillId: s.id }); }} aria-label={`${s.name} — open details`}>
                    <td><b>{s.name}</b><small>{s.root.name} · {num(s.attempts)} answers</small></td>
                    <td>{s.modes.length ? <span className="pr-modechips">{s.modes.slice(0, 3).map((m) => <em key={m}>{m}</em>)}</span> : <span className="pr-muted">—</span>}</td>
                    <td><span className="pr-cov"><span className="pr-seg-bar amber" aria-hidden="true">{Array.from({ length: 10 }, (_, i) => <i key={i} className={i < Math.round(s.coverage * 10) ? 'on' : ''} />)}</span>{pct(s.coverage)}</span></td>
                    <td className="pr-muted">{ago(s.last_seen_days)}</td>
                    <td><span className="pr-acc">{pct(s.correct / s.attempts)}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <p className="pu-empty">Nothing practised in the last 30 days — pick a skill on Practice to get going.</p>}
          <p className="pr-foot">Coverage is the share of the skill’s concepts you’ve answered questions on.</p>
        </section>

        <section className="pu-panel pr-growth" aria-labelledby="pr-gs">
          <div className="pr-sec-head"><div><h2 id="pr-gs">Growth status</h2><span className="pr-sub">{role ? `Your ${role.title} skills by level` : 'Your skills by level'}</span></div></div>
          <div className="pr-growth-body">
            <div className="pr-grow-ringwrap">
              <GrowthRing counts={growth} total={growthBase.length || 1} />
              <span><b>{growthBase.length}</b><small>skills</small></span>
            </div>
            <ul className="pr-grow-legend">
              {GROWTH.map((g) => <li key={g.id}><i className={`g-${g.id}`} />{g.label}<b>{growth[g.id] || 0}</b></li>)}
            </ul>
          </div>
          <div className="pr-grow-progress">
            <div className="pr-grow-track2"><i style={{ width: `${growthBase.length ? Math.round((started / growthBase.length) * 100) : 0}%` }} /><em style={{ left: `${Math.min(92, Math.max(8, growthBase.length ? Math.round((started / growthBase.length) * 100) : 0))}%` }}>{growthBase.length ? Math.round((started / growthBase.length) * 100) : 0}%</em></div>
            <div className="pr-grow-split"><span><i className="on" />Practising <b>{started}</b></span><span><i />Not started <b>{growthBase.length - started}</b></span></div>
          </div>
          <p className="pr-foot">Levels come from proficiency: Expert 80+, Proficient 65+, Competent 50+, Advanced beginner below 50; Novice means fewer than {MIN_EVIDENCE} graded answers.</p>
        </section>
      </div>

      {/* Where your skills lead — compact flow from your practice to job titles */}
      <section className="pu-panel pr-flowcard" aria-labelledby="pr-fl">
        <div className="pr-sec-head">
          <div>
            <h2 id="pr-fl">Where your skills lead</h2>
            <span className="pr-sub">The skills you’re working on, and the job titles they build toward</span>
          </div>
          <button type="button" className="pu-link" onClick={() => setExplore({})}>Explore learning memory <IconArrowRight size={12} /></button>
        </div>
        {flow.skills.length ? (
          <div className="pr-flow-grid">
            <aside className="pr-flow-side">
              <dl>
                <div><dt>Answers</dt><dd>{num(flow.answers)}</dd></div>
                <div><dt>Skills</dt><dd>{flow.skillCount}</dd></div>
                <div><dt>Job titles</dt><dd>{flow.roleCount}</dd></div>
              </dl>
              <p><b>{flow.skills[0].name}</b> is most of your practice{flow.roles[0] ? <> and mostly builds toward <b>{flow.roles[0].name}</b></> : null}.</p>
              <small>Select a skill for its topics.</small>
            </aside>
            <SkillFlow skills={flow.skills} roles={flow.roles} onSkill={(id) => setSheet({ skillId: id })} />
          </div>
        ) : <p className="pu-empty">Practise a few skills and you’ll see which job titles they build toward.</p>}
      </section>


        <section className="pu-panel pr-skills" aria-labelledby="pr-sk">
          <div className="pr-sec-head">
            <div><h2 id="pr-sk">Skills & topics</h2><span className="pr-sub">{role ? `The skills for ${role.title} · select one for its topics` : 'Skills you’ve practised · select one for its topics'}</span></div>
          </div>
          {role && (
            <ul className="pr-coverage" aria-label="Skill coverage">
              <li><b>{coverage.total}</b> skills for the role</li>
              <li><b>{coverage.assessed}</b> assessed</li>
              <li className="s-strong"><b>{coverage.strong}</b> strong</li>
              <li className="s-needs"><b>{coverage.needs}</b> need practice</li>
              <li className="s-not"><b>{coverage.notAssessed}</b> not assessed</li>
            </ul>
          )}
          {skillList.length ? (
            <table className="pr-table">
              <thead><tr><th>Skill</th><th>Proficiency</th><th>Accuracy</th><th>Answers</th><th>Topics</th><th>Last practised</th><th>Status</th></tr></thead>
              <tbody>
                {(allSkills ? skillList : skillList.slice(0, 7)).map((r) => (
                  <tr key={r.id} onClick={() => setSheet({ skillId: r.id })} tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter') setSheet({ skillId: r.id }); }} aria-label={`${r.name} — open details`}>
                    <td><b>{r.name}</b></td>
                    <td>{r.proficiency == null ? <span className="pr-muted">{r.attempts ? 'Not enough data' : '—'}</span> : <span className="pr-prof"><span className="pr-prof-bar"><i style={{ width: `${r.proficiency}%` }} className={`s-${r.status}`} /></span>{r.proficiency}</span>}</td>
                    <td>{r.accuracy == null ? '—' : pct(r.accuracy)}</td>
                    <td>{num(r.attempts)}</td>
                    <td>{r.topicsCovered}/{r.topicsTotal}{r.topicsAttention ? <small className="pr-warn"> · {r.topicsAttention} need attention</small> : null}</td>
                    <td className="pr-muted">{r.last == null ? '—' : ago(r.last)}</td>
                    <td><StatusTag s={r.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <p className="pu-empty">Practise a topic and your skills appear here.</p>}
          {skillList.length > 7 && <button type="button" className="pu-link" onClick={() => setAllSkills((v) => !v)}>{allSkills ? 'Show fewer' : `View all ${skillList.length} skills`}</button>}
          <p className="pr-foot">Proficiency blends accuracy, depth reached and coverage, discounted when practice is stale — shown after {MIN_EVIDENCE}+ graded answers.</p>
        </section>


      {/* 5 · Interview readiness */}
      <section className="pu-panel pr-ready" id="pr-ready" aria-labelledby="pr-rd">
        <div className="pr-sec-head">
          <div><h2 id="pr-rd">{role ? `${role.title} — interview readiness` : 'Interview readiness'}</h2><span className="pr-sub">Evidence from your practice{interviews.length ? ' and interviews' : ''} — an estimate, not a guarantee.</span></div>
          <button type="button" className="pu-link" onClick={onOpenInterviews}>Interviews <IconArrowRight size={12} /></button>
        </div>
        {!role ? <p className="pu-empty">Pick a goal on Home to see readiness for a role.</p> : (
          <div className="pr-ready-grid">
            <div className="pr-ready-score">
              {readiness.score != null ? (
                <>
                  <b>{readiness.score}<small>/100</small></b>
                  <span>Based on {readiness.assessedN} assessed skill{readiness.assessedN === 1 ? '' : 's'} of {coverage.total}{readiness.usesInterviews ? ' and your interviews for this role' : ''}.</span>
                </>
              ) : (
                <>
                  <b className="pending">In progress</b>
                  <span>Assess {readiness.need - readiness.assessedN} more skill{readiness.need - readiness.assessedN === 1 ? '' : 's'} for an estimate{readiness.nextToAssess.length ? ` — e.g. ${readiness.nextToAssess.map((r) => r.name).join(', ')}` : ''}.</span>
                </>
              )}
              <ul className="pr-legend">
                <li><i className="s-strong" />Strong</li><li><i className="s-developing" />Developing</li><li><i className="s-needs" />Needs practice</li><li><i className="s-not" />Not assessed</li>
              </ul>
            </div>
            <Radar rows={rows} />
            <div className="pr-ready-side">
              <h3>Interview performance</h3>
              {interviews.length ? (
                <dl className="pr-kv">
                  <div><dt>Interviews completed</dt><dd>{interviews.length}</dd></div>
                  {aiIv.length > 0 && <div><dt>AI interviews · average</dt><dd>{avgOf(aiIv) ?? '—'}/100</dd></div>}
                  {wrIv.length > 0 && <div><dt>Written · average</dt><dd>{avgOf(wrIv) ?? '—'}/100</dd></div>}
                  <div><dt>Most recent</dt><dd>{interviews[0].score == null ? '—' : `${interviews[0].score}/100`}</dd></div>
                  {recurring && <div><dt>Recurring gap</dt><dd>{recurring.topic}</dd></div>}
                </dl>
              ) : <p className="pr-sub">No interviews yet — an AI interview shows how your practice holds up out loud.</p>}
              {(() => {
                const next = rows.filter((r) => r.status === 'needs')[0] || rows.find((r) => r.status === 'not' || r.status === 'early');
                return next ? (
                  <div className="pr-next">
                    <small>Next priority</small><b>{next.name}</b>
                    <span>{next.status === 'needs' ? `Proficiency ${next.proficiency}/100 — an improvement area for this role.` : 'Important for this role and not assessed yet.'}</span>
                    <div>
                      <button type="button" className="pu-btn pu-btn-sm pu-btn-primary" onClick={() => practiceWith({ id: next.id, name: next.name })}>Practise {next.name}</button>
                      <button type="button" className="pu-link" onClick={() => onOpenHubNode?.(next.id)}>Review concepts</button>
                      <button type="button" className="pu-link" onClick={onStartInterview}>Try an AI interview</button>
                    </div>
                  </div>
                ) : null;
              })()}
            </div>
          </div>
        )}
      </section>

      {/* 6 · Strengths & areas to improve */}
      <div className="pr-row even">
        <section className="pu-panel" aria-labelledby="pr-str">
          <div className="pr-sec-head"><h2 id="pr-str">Strengths</h2></div>
          {strengths.length ? (
            <ul className="pr-ev">
              {strengths.map((s) => (
                <li key={s.node.id}>
                  <div><b>{s.node.name}</b><small>{trail(s.node.id)}</small></div>
                  <ul>{s.evidence.map((e) => <li key={e}>{e}</li>)}</ul>
                  <button type="button" className="pu-link" onClick={() => harder(s.node)}>Try harder questions <IconArrowRight size={12} /></button>
                </li>
              ))}
            </ul>
          ) : <p className="pu-empty">Strengths appear once a topic has {MIN_EVIDENCE}+ graded answers at 75%+ accuracy.</p>}
        </section>
        <section className="pu-panel" aria-labelledby="pr-imp">
          <div className="pr-sec-head"><h2 id="pr-imp">Areas to improve</h2></div>
          {improve.length ? (
            <ul className="pr-ev">
              {improve.map((s) => (
                <li key={s.node.id}>
                  <div><b>{s.node.name}</b><small>{trail(s.node.id)}</small></div>
                  <ul>{s.gaps.map((g) => <li key={g.type}><span className="pr-gap">{g.type}</span> {g.why}</li>)}</ul>
                  <div className="pr-acts">
                    <button type="button" className="pu-link" onClick={() => onOpenHubNode?.(s.node.id)}>Review concepts</button>
                    <button type="button" className="pu-link" onClick={() => practiceWith(s.node, 'mcq')}>Practise MCQs</button>
                    <button type="button" className="pu-link" onClick={() => practiceWith(s.node, 'flashcards')}>Revise flashcards</button>
                  </div>
                </li>
              ))}
            </ul>
          ) : <p className="pu-empty">No evidence-backed gaps right now. Topics you haven’t attempted aren’t counted as weaknesses.</p>}
        </section>
      </div>

      {/* 7 · Practice performance */}
      <section className="pu-panel pr-perf" aria-labelledby="pr-pf">
        <div className="pr-sec-head"><h2 id="pr-pf">Practice performance</h2></div>
        <div className="pr-perf-grid">
          <div>
            <h3>By practice mode</h3>
            {modes.length ? (
              <ul className="pr-modes">
                {modes.map((m) => (
                  <li key={m.mode}>
                    <span><b>{m.mode}</b><small>{num(m.n)} {m.unit}</small></span>
                    <span className="pr-seg-bar" aria-hidden="true">{Array.from({ length: 10 }, (_, i) => <i key={i} className={m.v != null && i < Math.round(m.v * 10) ? 'on' : ''} />)}</span>
                    <em>{m.metric || '—'}</em>
                  </li>
                ))}
              </ul>
            ) : <p className="pu-empty">No practice recorded yet.</p>}
          </div>
          <div>
            <h3>By difficulty</h3>
            {diff.n >= 8 ? (
              <ul className="pr-diff">
                {diff.rows.map((r) => (
                  <li key={r.label}><span>{r.label}</span><span className="pr-prof-bar"><i style={{ width: `${r.accuracy == null ? 0 : Math.round(r.accuracy * 100)}%` }} /></span><b>{r.accuracy == null ? '—' : pct(r.accuracy)}</b><small>{r.attempts}</small></li>
                ))}
              </ul>
            ) : <p className="pr-sub">Not enough data yet — difficulty is recorded on recent answers ({diff.n} so far).</p>}
          </div>
          <div>
            <h3>Consistency</h3>
            <dl className="pr-kv">
              <div><dt>Active days</dt><dd>{days.size}</dd></div>
              <div><dt>Sessions started</dt><dd>{totals.sessions}</dd></div>
              <div><dt>Sessions per active week</dt><dd>{weeksActive ? (totals.sessions / weeksActive).toFixed(1) : '—'}</dd></div>
              <div><dt>Longest break</dt><dd>{gaps.length > 1 ? `${longestBreak} days` : '—'}</dd></div>
              <div><dt>Average session length</dt><dd className="muted">Not tracked</dd></div>
            </dl>
          </div>
        </div>
      </section>

      {/* 8 · Insights + next steps */}
      <div className="pr-row">
        <section className="pu-panel pr-insights" aria-labelledby="pr-ins">
          <div className="pr-sec-head"><h2 id="pr-ins">Insights</h2></div>
          {insights.length ? (
            <ul>
              {insights.map((i) => (
                <li key={i.key} className={`t-${i.tone}`}>
                  <b>{i.title}</b><span>{i.why}</span>
                  {i.action && <button type="button" className="pu-link" onClick={i.action.run}>{i.action.label} <IconArrowRight size={12} /></button>}
                </li>
              ))}
            </ul>
          ) : <p className="pu-empty">Insights appear after a couple of weeks of practice.</p>}
        </section>
        <section className="pu-panel pr-nextsteps" aria-labelledby="pr-nx">
          <div className="pr-sec-head"><h2 id="pr-nx">Recommended next steps</h2></div>
          <ol>
            {nextActions.map((a) => (
              <li key={a.key}>
                <b>{a.title}</b><span>{a.why}</span>
                <div className="pr-acts">{a.acts.map(([l, run], i) => <button key={l} type="button" className={i === 0 ? 'pu-btn pu-btn-sm pu-btn-primary' : 'pu-link'} onClick={run}>{l}</button>)}</div>
              </li>
            ))}
          </ol>
          {!nextActions.length && <p className="pu-empty">Practise a few topics and your next steps appear here.</p>}
        </section>
      </div>

      {/* 9 · Goals + 10 · recent activity */}
      <div className="pr-row">
        <section className="pu-panel pr-goals" aria-labelledby="pr-gl">
          <div className="pr-sec-head"><h2 id="pr-gl">Goals</h2>{goalPath && <button type="button" className="pu-link" onClick={() => onOpenPath?.(goalPath.id)}>Learning path <IconArrowRight size={12} /></button>}</div>
          {goalPath ? (
            <p className="pr-goal"><b>{goalPath.title}</b>{role ? ` · ${coverage.assessed} of ${coverage.total} skills assessed, ${coverage.strong} strong, ${coverage.needs} need practice, ${coverage.notAssessed} still to assess` : ''}</p>
          ) : <p className="pr-sub">No goal set — choose one on Home.</p>}
          <h3>This week</h3>
          {[['weeklyDays', 'Practise on', 'days', daysThisWeek, [2, 3, 4, 5, 6, 7]], ['weeklyQuestions', 'Complete', 'questions', thisWk?.attempts || 0, [20, 30, 50, 75, 100]]].map(([k, l, unit, cur, opts]) => {
            const target = prefs[k];
            const p = Math.min(1, cur / target);
            return (
              <div key={k} className="pr-goalrow">
                <div className="pr-goalrow-head">
                  <span>{l} <select value={target} onChange={(e) => setPref({ [k]: Number(e.target.value) })} aria-label={`${l} target`}>{opts.map((o) => <option key={o} value={o}>{o}</option>)}</select> {unit}</span>
                  <b>{cur}/{target}</b>
                </div>
                <span className="pr-prof-bar wide"><i style={{ width: `${Math.round(p * 100)}%` }} className={p >= 1 ? 's-strong' : ''} /></span>
              </div>
            );
          })}
          <p className="pr-foot">Targets are saved on this device. Learning-time goals need time tracking, which isn’t available yet.</p>
        </section>

        <section className="pu-panel pr-recent" aria-labelledby="pr-rc">
          <div className="pr-sec-head"><h2 id="pr-rc">Recent activity</h2><button type="button" className="pu-link" onClick={() => setActivityAll(true)}>View activity <IconArrowRight size={12} /></button></div>
          {activity.length ? (
            <ul className="pr-feed">
              {activity.slice(0, 6).map((a) => (
                <li key={a.key}>
                  <span className="pr-feed-mode">{a.mode}</span>
                  <button type="button" onClick={() => (a.cid ? onOpenHubNode?.(a.cid) : null)} disabled={!a.cid}>{a.text}</button>
                  <time>{ago((Date.now() - Date.parse(a.ts)) / 86400000)}</time>
                </li>
              ))}
            </ul>
          ) : <p className="pu-empty">Your recent practice appears here.</p>}
        </section>
      </div>

    </>,
    <>
      {/* Skill / topic detail */}
      {sheet && sheetSkill && (
        <div className="pr-sheet-veil" role="presentation" onMouseDown={(e) => { if (e.target === e.currentTarget) setSheet(null); }}>
          <aside className="pr-sheet" role="dialog" aria-modal="true" aria-labelledby="pr-sheet-h">
            <div className="pr-sheet-head">
              <div>
                <nav className="kh-crumbs" aria-label="Level">
                  {sheetTopic ? <button type="button" onClick={() => setSheet({ skillId: sheetSkill.id })}>{sheetSkill.name}</button> : <span aria-current="page">{trail(sheetSkill.id) || 'Skill'}</span>}
                  {sheetTopic && <><IconChevRight size={12} /><span aria-current="page">{sheetTopic.name}</span></>}
                </nav>
                <h2 id="pr-sheet-h">{(sheetTopic || sheetSkill).name}</h2>
              </div>
              <button type="button" className="kh-icon-btn" onClick={() => setSheet(null)} aria-label="Close"><IconX size={18} /></button>
            </div>
            {(() => {
              const n = sheetTopic || sheetSkill;
              const p = proficiency(n);
              const kidsList = n.children || [];
              const practised = kidsList.filter((t) => t.attempts >= 3);   // need a few answers before calling a topic strongest / weakest
              const byAcc = [...practised].sort((a, b) => b.correct / b.attempts - a.correct / a.attempts);
              const g = gapsOf(n, due);
              const dueHere = due.filter((d) => String(d.concept_id).startsWith(`${n.id}.`));
              return (
                <>
                  <dl className="pr-sheet-stats">
                    <div><dt>Proficiency</dt><dd>{p == null ? <span className="muted">Not enough data</span> : <>{p}<small>/100</small></>}</dd></div>
                    <div><dt>Accuracy</dt><dd>{n.attempts ? pct(n.correct / n.attempts) : '—'}</dd></div>
                    <div><dt>Answers</dt><dd>{num(n.attempts)}</dd></div>
                    <div><dt>Last practised</dt><dd>{n.attempts ? ago(n.last_seen_days) : '—'}</dd></div>
                    {sheetSkillRow && !sheetTopic && <div><dt>Topics covered</dt><dd>{sheetSkillRow.topicsCovered}/{sheetSkillRow.topicsTotal}</dd></div>}
                    {n.depth_max > 0 && <div><dt>Depth reached</dt><dd>{n.depth_reached || 0} of {n.depth_max}</dd></div>}
                  </dl>
                  {g.length > 0 && <ul className="pr-ev compact">{g.map((x) => <li key={x.type}><span className="pr-gap">{x.type}</span> {x.why}</li>)}</ul>}
                  {(n.gaps || []).length > 0 && <><h3>Missed recently</h3><ul className="pr-bullets">{n.gaps.map((x, i) => <li key={i}>{x}</li>)}</ul></>}
                  {practised.length > 1 && (
                    <div className="pr-sheet-two">
                      <div><h3>Strongest</h3><ul className="pr-bullets">{byAcc.slice(0, 2).map((t) => <li key={t.id}>{t.name} · {pct(t.correct / t.attempts)}</li>)}</ul></div>
                      <div><h3>Weakest</h3><ul className="pr-bullets">{byAcc.slice(-2).reverse().map((t) => <li key={t.id}>{t.name} · {pct(t.correct / t.attempts)}</li>)}</ul></div>
                    </div>
                  )}
                  {dueHere.length > 0 && <><h3>Needs revision</h3><ul className="pr-bullets">{dueHere.slice(0, 4).map((d) => <li key={d.concept_id}>{nameOf(d.concept_id)} · {Math.round(d.last_seen_days)} days ago</li>)}</ul></>}
                  {kidsList.length > 0 && (
                    <>
                      <h3>{sheetTopic ? 'Concepts' : 'Topics'}</h3>
                      <ul className="pr-topics">
                        {kidsList.map((t) => (
                          <li key={t.id}>
                            <button type="button" onClick={() => (sheetTopic || !(t.children || []).length ? setOpen(t) : setSheet({ skillId: sheetSkill.id, topicId: t.id }))}>
                              <span>{t.name}</span><StatusTag s={statusOf(t)} /><small>{t.attempts ? `${pct(t.correct / t.attempts)} · ${t.attempts}` : '—'}</small><IconChevRight size={13} />
                            </button>
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                  <div className="pr-sheet-acts">
                    <button type="button" className="pu-btn pu-btn-primary" onClick={() => practiceWith(n)}>Practice this {sheetTopic ? 'topic' : 'skill'}</button>
                    <button type="button" className="pu-btn pu-btn-quiet" onClick={() => practiceWith(n, 'flashcards')}>Review flashcards</button>
                    <button type="button" className="pu-link" onClick={() => harder(n)}>Attempt a harder question</button>
                    <button type="button" className="pu-link" onClick={() => onOpenHubNode?.(n.id)}>Open in Knowledge Hub</button>
                    <button type="button" className="pu-link" onClick={() => practiceDraft(n)}>Plan a session</button>
                  </div>
                </>
              );
            })()}
          </aside>
        </div>
      )}

      {/* Full activity */}
      {activityAll && (
        <div className="kh-modal-veil" onMouseDown={(e) => { if (e.target === e.currentTarget) setActivityAll(false); }}>
          <div className="kh-modal pr-actmodal" role="dialog" aria-modal="true" aria-labelledby="pr-am-h">
            <div className="kh-modal-head"><div><h2 id="pr-am-h">Learning history</h2><p>Your most recent practice and interviews.</p></div><button type="button" className="kh-icon-btn" onClick={() => setActivityAll(false)} aria-label="Close"><IconX size={18} /></button></div>
            <div className="pr-am-filters">
              <label className="kh-select"><span className="pu-sr">Activity type</span><select value={actMode} onChange={(e) => setActMode(e.target.value)} aria-label="Activity type">{actModes.map((m) => <option key={m}>{m}</option>)}</select></label>
              <label className="kh-select"><span className="pu-sr">Skill</span><select value={actSkill} onChange={(e) => setActSkill(e.target.value)} aria-label="Skill"><option value="">All skills</option>{actSkills.map((m) => <option key={m}>{m}</option>)}</select></label>
            </div>
            <ul className="pr-feed">
              {actShown.map((a) => (
                <li key={a.key}><span className="pr-feed-mode">{a.mode}</span><button type="button" onClick={() => (a.cid ? onOpenHubNode?.(a.cid) : null)} disabled={!a.cid}>{a.text}</button><time>{new Date(a.ts).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' })}</time></li>
              ))}
              {(actMode === 'All' || actMode === 'Interviews') && !actSkill && interviews.map((e) => (
                <li key={e.id}><span className="pr-feed-mode">Interviews</span><span>{e.title} · {typeLabel(e.kind)}{e.score != null ? ` · ${e.score}/100` : ''}</span><time>{new Date(e.ts).toLocaleDateString()}</time></li>
              ))}
              {!actShown.length && !(interviews.length && (actMode === 'All' || actMode === 'Interviews')) && <li className="pr-sub">Nothing matches these filters.</li>}
            </ul>
            <p className="pr-foot">The engine keeps your 25 most recent activities; older practice is summarised per topic in Skills.</p>
          </div>
        </div>
      )}

      {open && (
        <div className="mem-veil pg-drawer-veil" onClick={() => setOpen(null)} role="presentation">
          <div className="mem-drawer" onClick={(e) => e.stopPropagation()} role="dialog" aria-label={open.name}>
            <div className="mem-dr-head">
              <div><div className="mem-dr-title">{open.name}</div><div className="mem-dr-sub">{st(open.status).word} · last seen {ago(open.last_seen_days)}</div></div>
              <button className="mem-dr-x" type="button" onClick={() => setOpen(null)} aria-label="Close">✕</button>
            </div>
            <div className="mem-dr-stats">
              <div className="mem-stat"><b>{pct(open.mastery)}</b><span>mastery</span></div>
              <div className="mem-stat"><b>{open.correct}/{open.attempts}</b><span>right</span></div>
              <div className="mem-stat"><b>{open.hints || 0}</b><span>hints</span></div>
            </div>
            {open.depth_max > 0 && (
              <div className="mem-dr-sec"><div className="mem-dr-h">How deep you've gone</div>
                <div className="mem-ladder"><DepthDots reached={open.depth_reached} max={open.depth_max} /><span className="mem-ladder-label">{open.depth_reached || 0} of {open.depth_max} levels — foundational → follow-ups → nuance</span></div>
              </div>
            )}
            {(open.gaps || []).length > 0 && (
              <div className="mem-dr-sec"><div className="mem-dr-h">What you missed recently</div><ul className="mem-gaps">{open.gaps.map((g, i) => <li key={i}>{g}</li>)}</ul></div>
            )}
            <div className="pg-drawer-actions">
              <button className="pu-btn pu-btn-primary" type="button" onClick={() => { const n = open; setOpen(null); practiceWith(n); }}>Practice this</button>
              <button className="pu-btn pu-btn-quiet" type="button" onClick={() => { const n = open; setOpen(null); setExplore(null); onOpenHubNode?.(n.id); }}>Read in Knowledge Hub</button>
              <button className="pu-link" type="button" onClick={() => practiceDraft(open)}>Plan a session on it</button>
            </div>
          </div>
        </div>
      )}
    </>,
  );
}
